import axios from 'axios';

/**
 * Base path of the JAPI AI Assistant contract. JAPI is the only client facing entry point, so
 * these calls go through the default (JAPI) axios base url and bearer token.
 *
 * @type {string}
 */
export const AI_ASSISTANT_BASE_PATH = '/v2/ai-assistant';

/**
 * Submit a prompt. Pass `conversationId` to continue an existing conversation, omit it to start a
 * new one.
 *
 * @method submitAiAssistantMessageAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const submitAiAssistantMessageAPI = ({ message, conversationId = null } = {}) => {
    const url = conversationId
        ? `${AI_ASSISTANT_BASE_PATH}/conversations/${conversationId}/messages`
        : `${AI_ASSISTANT_BASE_PATH}/messages`;

    return axios({
        method: 'post',
        url,
        data: { message },
    });
};

/**
 * Like or dislike one conversation (answer) of a chat.
 *
 * @method saveAiAssistantFeedbackAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const saveAiAssistantFeedbackAPI = ({ userId, chatId, conversationId, feedback } = {}) =>
    axios({
        method: 'patch',
        url: `/v2/users/${userId}/ai-chats/${chatId}/conversations/${conversationId}`,
        data: { feedback },
    });

/**
 * Read the caller's current feedback on one answer, so the tray can restore the like/dislike
 * state.
 *
 * @method getAiAssistantFeedbackAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const getAiAssistantFeedbackAPI = ({ messageId } = {}) =>
    axios({
        method: 'get',
        url: `${AI_ASSISTANT_BASE_PATH}/messages/${messageId}/feedback`,
    });

/**
 * Upstream availability for the UI.
 *
 * @method getAiAssistantHealthAPI
 * @return {Promise}
 */
export const getAiAssistantHealthAPI = () =>
    axios({
        method: 'get',
        url: `${AI_ASSISTANT_BASE_PATH}/health`,
    });

/**
 * Frequently asked questions rendered as the primary screen's suggestion chips.
 *
 * @method getAiAssistantSuggestionsAPI
 * @param  {string}  userId - The user ID
 * @return {Promise} Response containing array of FAQ suggestions [{id, question}, ...]
 */
export const getAiAssistantSuggestionsAPI = userId =>
    axios({
        method: 'get',
        url: `/v2/users/${userId}/ai-chats/faq-suggestions`,
    });

/**
 * API to create session.
 *
 * @method createAISessionAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const createAISessionAPI = data =>
    axios({
        method: 'post',
        url: '/chat/sessions',
        data,
        type: 'aiAssistant',
        headers: {
            'X-Guardrail-Complexity': 'DISABLED',
        },
    });

/**
 * Timeout in milliseconds for the send message API request.
 *
 * @type {number}
 */
export const SEND_MESSAGE_TIMEOUT_MS = 8000;

/**
 * API to send message.
 *
 * @method sendMessageAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const sendMessageAPI = data => {
    const { chatId } = data;
    return axios({
        method: 'post',
        url: `/chat/sessions/${chatId}/messages`,
        data,
        type: 'aiAssistant',
        timeout: SEND_MESSAGE_TIMEOUT_MS,
    });
};

/**
 * API to get status answer.
 *
 * @method getAnswerAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const getAnswerAPI = data => {
    const { chatId, messageId } = data;
    return axios({
        method: 'get',
        url: `/chat/sessions/${chatId}/messages/${messageId}`,
        data,
        type: 'aiAssistant',
    });
};

/**
 * API to create feedback.
 *
 * @method createFeedbackAPI
 * @param  {Object}  data
 * @return {Promise}
 */
export const createFeedbackAPI = data => {
    return axios({
        method: 'post',
        url: `/chat/feedback`,
        data,
        type: 'aiAssistant',
    });
};

/**
 * Timeout in milliseconds for the close AI session API request.
 *
 * @type {number}
 */
export const CLOSE_AI_SESSION_TIMEOUT_MS = 10000;

/**
 *  Close Session.
 *
 * @param {Object}    sessionId
 * @return {Promise}
 */
export const closeAISessionAPI = sessionId =>
    axios({
        method: 'delete',
        url: `/chat/sessions/${sessionId}`,
        type: 'aiAssistant',
        timeout: CLOSE_AI_SESSION_TIMEOUT_MS,
    });

/**
 * Send email in UI when user logs out.
 *
 * @method sendEmailCloseAISession
 * @param  {Object}  data
 * @return {Promise}
 */
export const sendEmailCloseAISession = data => {
    return axios({
        method: 'post',
        url: `/v2/users/send-email`,
        data,
    });
};

/**
 *  Close all AISession API the user.
 *
 * @param {Object}    userId
 * @return {Promise}
 */
export const closeAllAISessionAPI = userId =>
    axios({
        method: 'delete',
        url: `/chat/sessions?userId=${userId}`,
        type: 'aiAssistant',
    });

/**
 * API to get hitory chat.
 *
 * @method getHistoryChatAPI
 * @param  {string}  chatSessionId
 * @return {Promise}
 */
export const getHistoryChatAPI = chatSessionId => {
    return axios({
        method: 'get',
        url: `chat/sessions/${chatSessionId}/details`,
        type: 'aiAssistant',
    });
};

/**
 * API to get chat session details.
 *
 * @method getChatSessionDetailsAPI
 * @param  {string}  chatSessionId
 * @return {Promise}
 */
export const getChatSessionDetailsAPI = chatSessionId => {
    return axios({
        method: 'get',
        url: `chat/sessions/${chatSessionId}/details`,
        type: 'aiAssistant',
    });
};

/**
 * API to get chat sessions by UUID.
 *
 * @method getChatSessionsByIdAPI
 * @param  {string}  logId - Single logId linked to multiple sessions
 * @return {Promise}
 */
export const getChatSessionsByIdAPI = logId => {
    return axios({
        method: 'get',
        url: `/chat/sessions/log/${logId}`,
        type: 'aiAssistant',
    });
};

/**
 * API to get paginated chat history by userId.
 *
 * @method getChatHistoryPaginatedAPI
 * @param  {Object}  params - Query parameters (userId, page, limit, etc.)
 * @return {Promise}
 */
export const getChatHistoryPaginatedAPI = params => {
    return axios({
        method: 'get',
        url: '/chat/sessions/paginated',
        params,
        type: 'aiAssistant',
    });
};

/**
 * Save the user's AI policy acceptance.
 * Records the acceptance timestamp in sysadm_user_settings.ai_policy_accepted_at
 *
 * @method saveAiPolicyAcceptanceAPI
 * @param  {string}  userId - The user ID
 * @return {Promise}
 */
export const saveAiPolicyAcceptanceAPI = userId =>
    axios({
        method: 'patch',
        url: `/v2/users/${userId}/settings/ai-policy-accepted`,
    });

/**
 * Get FAQ suggestions for the AI Assistant.
 *
 * @method getAiAssistantFaqsAPI
 * @param  {string}  userId - The user ID
 * @return {Promise} Response containing array of FAQ suggestions [{id, question}, ...]
 */
export const getAiAssistantFaqsAPI = userId =>
    axios({
        method: 'get',
        url: `/v2/users/${userId}/ai-chats/faq-suggestions`,
    });

/**
 * Get all active chats for a user.
 *
 * @method getAiAssistantChatsAPI
 * @param  {string}  userId - The user ID
 * @return {Promise} Response containing array of chat objects
 */
export const getAiAssistantChatsAPI = userId =>
    axios({
        method: 'get',
        url: `/v2/users/${userId}/ai-chats`,
    });

/**
 * Get a single chat by ID.
 *
 * @method getAiAssistantChatByIdAPI
 * @param  {string}  userId - The user ID
 * @param  {string}  chatId - The chat ID
 * @return {Promise} Response containing chat with conversations
 */
export const getAiAssistantChatByIdAPI = (userId, chatId) =>
    axios({
        method: 'get',
        url: `/v2/users/${userId}/ai-chats/${chatId}`,
    });

/**
 * Create a new chat for a user.
 *
 * @method createAiAssistantChatAPI
 * @param  {string}  userId - The user ID
 * @param  {Object}  data - { message: string }
 * @return {Promise} Response containing new chat and assistant response
 */
export const createAiAssistantChatAPI = (userId, data) =>
    axios({
        method: 'post',
        url: `/v2/users/${userId}/ai-chats`,
        data,
    });

/**
 * Continue an existing chat with a new message.
 *
 * @method continueAiAssistantChatAPI
 * @param  {string}  userId - The user ID
 * @param  {string}  chatId - The chat ID
 * @param  {Object}  data - { message: string }
 * @return {Promise} Response containing updated conversation and assistant response
 */
export const continueAiAssistantChatAPI = (userId, chatId, data) =>
    axios({
        method: 'post',
        url: `/v2/users/${userId}/ai-chats/${chatId}/conversation`,
        data,
    });

/**
 * Export the full result set of one answer as CSV.
 *
 * @method exportAiAssistantConversationAPI
 * @param  {string}  userId - The user ID
 * @param  {string}  chatId - The chat ID
 * @param  {string}  conversationId - The conversation (answer) ID
 * @return {Promise} Response whose `data` is the export manifest `{ status, files, zipUrl }`
 */
export const exportAiAssistantConversationAPI = (userId, chatId, conversationId) =>
    axios({
        method: 'post',
        url: `/v2/users/${userId}/ai-chats/${chatId}/conversations/${conversationId}/export`,
    });

/**
 * Rename a chat.
 *
 * @method renameAiAssistantChatAPI
 * @param  {string}  userId - The user ID
 * @param  {string}  chatId - The chat ID
 * @param  {Object}  data - { chatName: string }
 * @return {Promise}
 */
export const renameAiAssistantChatAPI = (userId, chatId, data) =>
    axios({
        method: 'patch',
        url: `/v2/users/${userId}/ai-chats/${chatId}`,
        data,
    });

/**
 * Soft delete a chat (set status to INACTIVE).
 *
 * @method deleteAiAssistantChatAPI
 * @param  {string}  userId - The user ID
 * @param  {string}  chatId - The chat ID
 * @return {Promise}
 */
export const deleteAiAssistantChatAPI = (userId, chatId) =>
    axios({
        method: 'patch',
        url: `/v2/users/${userId}/ai-chats/${chatId}`,
        data: { status: 'INACTIVE' },
    });
